import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import Icon from '@/components/Icon'
import Price from '@/components/Price'

import useCheckout from '../../hooks/useCheckout'
import type { CartItem } from '../../store/checkoutSlice'
import Step from '../Step/Step'
import {
  CartCard,
  CartCardHeader,
  CartCardHeaderContent,
  CartCardInner,
  CartCardMedia,
  CartCardMeta,
  CartCardTitle,
  CartEmpty,
  CartGroupBody,
  CartGroups,
  CartHint,
  CartRemoveButton,
  CartRow,
  CartRowName,
  CartRowPrice,
  CartRows,
  CartSimplePrice,
  CartTitle,
  CartTotal,
  CartWrapper
} from './Cart.styles'

interface CartGroup {
  key: string
  productName: string
  coverImageUrl?: string
  date: string
  startTime: string | null
  hasConsumerCategories: boolean
  items: CartItem[]
  total: number
}

const formatSelection = (date: string, time: string | null, language: string) => {
  const parsedDate = new Date(`${date}T00:00:00`)
  if (Number.isNaN(parsedDate.getTime())) {
    return time || date
  }

  const locale = language.startsWith('pl') ? 'pl-PL' : 'en-US'
  const formattedDate = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long'
  }).format(parsedDate)

  if (!time) {
    return formattedDate
  }

  return `${formattedDate}, ${time}`
}

const formatMoney = (value: number, currency: string) => `${Number(value).toFixed(2)} ${currency === 'PLN' ? 'zł' : currency}`

const CheckoutCart = () => {
  const { t, i18n } = useTranslation()
  const { cart, removeFromCart } = useCheckout()

  const groupedItems = useMemo<CartGroup[]>(() => {
    const groups = new Map<string, CartGroup>()

    cart.forEach(item => {
      const key = `${String(item.productId)}:${item.date}:${item.startTime ?? 'whole-day'}`
      const existingGroup = groups.get(key)

      if (existingGroup) {
        existingGroup.items.push(item)
        existingGroup.total += item.qty * item.price.grossAmount
        existingGroup.hasConsumerCategories = existingGroup.hasConsumerCategories || item.consumerCategoryId !== null
        if (!existingGroup.coverImageUrl && item.coverImageUrl) {
          existingGroup.coverImageUrl = item.coverImageUrl
        }
        return
      }

      groups.set(key, {
        key,
        productName: item.productName || item.name,
        coverImageUrl: item.coverImageUrl,
        date: item.date,
        startTime: item.startTime,
        hasConsumerCategories: item.consumerCategoryId !== null,
        items: [item],
        total: item.qty * item.price.grossAmount
      })
    })

    return Array.from(groups.values())
  }, [cart])

  const handleRemoveGroup = (group: CartGroup) => {
    group.items.forEach(item => {
      removeFromCart({ id: item.id, all: true })
    })
  }

  return (
    <Step padded={true}>
      <CartWrapper>
        <CartTitle>{t('yourCart')}</CartTitle>

        {groupedItems.length === 0 && <CartEmpty>{t('noItemsInCart')}</CartEmpty>}

        <CartGroups>
          {groupedItems.map(group => {
            const firstItem = group.items[0]
            const currency = firstItem?.price.currency || 'PLN'
            const selectionLabel = group.date ? formatSelection(group.date, group.startTime, i18n.language) : null

            return (
              <CartCard key={group.key}>
                <CartRemoveButton onClick={() => handleRemoveGroup(group)} aria-label={t('remove')}>
                  <Icon name="trash" />
                </CartRemoveButton>
                <CartCardInner>
                  <CartCardMedia>{group.coverImageUrl && <img src={group.coverImageUrl} alt={group.productName} />}</CartCardMedia>
                  <CartGroupBody>
                    <CartCardHeader>
                      <CartCardHeaderContent>
                        <CartCardTitle>{group.productName}</CartCardTitle>
                        {selectionLabel && (
                          <CartCardMeta>
                            <Icon name="clock" />
                            <span>{selectionLabel}</span>
                          </CartCardMeta>
                        )}
                      </CartCardHeaderContent>
                      {!group.hasConsumerCategories && (
                        <CartSimplePrice>
                          <Price currency={currency}>{group.total}</Price>
                        </CartSimplePrice>
                      )}
                    </CartCardHeader>

                    {group.hasConsumerCategories && (
                      <>
                        <CartHint>{t('yourTickets')}</CartHint>
                        <CartRows>
                          {group.items.map(item => (
                            <CartRow key={String(item.id)}>
                              <CartRowName>{item.name}</CartRowName>
                              <CartRowPrice>{`${item.qty} x ${formatMoney(item.price.grossAmount, item.price.currency)}`}</CartRowPrice>
                            </CartRow>
                          ))}
                        </CartRows>
                        <CartTotal>
                          <span>{t('total')}:</span>
                          <Price currency={currency}>{group.total}</Price>
                        </CartTotal>
                      </>
                    )}
                  </CartGroupBody>
                </CartCardInner>
              </CartCard>
            )
          })}
        </CartGroups>
      </CartWrapper>
    </Step>
  )
}

export default CheckoutCart
